import type { FormRenderProps } from "@tql/ui-components-react/kendo/form";
/* eslint-disable eslint-comments/disable-enable-pair -- kendo skeleton */
/* eslint-disable @typescript-eslint/no-deprecated -- kendo skeleton */
import { Skeleton } from "@tql/ui-components-react/kendo/indicators";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { Form, FormElement } from "@tql/ui-components-react/tql/form/beta";
import { plusCircle } from "@tql/ui-components-react/untitled/icons";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ConsentStatement } from "~/shared/components/consent-statement/consent-statement.tsx";
import {
	FieldType,
	ValidationInputField,
} from "~/shared/components/fields/validation-input-field.tsx";
import { TraxCard } from "~/shared/components/trax-card/trax-card.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { useGetCurrentUserInfo } from "~/shared/hooks/use-current-user-info.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";

import { useUpdateWebUser } from "./hooks/use-current-user-info.ts";
import {
	useAddMfaPhoneNumber,
	useDisableMfa,
	useEnableMfa,
	useGetMfaPhoneNumbers,
	useInvalidateMfaQueries,
	useRemoveMfaPhoneNumber,
} from "./hooks/use-mfa.ts";
import styles from "./index.module.css";
import {
	formatPhoneForApi,
	maskPhoneDisplay,
	PHONE_MASK,
} from "./schemas/mfa-schema.ts";
import type { UpdateWebUserBody } from "./schemas/user-info-schema.ts";

type TermsConsentProps = {
	required?: boolean;
	validator?: (value: boolean) => string;
};

export const TermsConsent = ({ required, validator }: TermsConsentProps) => {
	const privacyUrl = "https://www.tql.com/privacy";
	const termsUrl = "https://www.tql.com/terms";
	return (
		<ValidationInputField
			fieldType={FieldType.CHECKBOX}
			id={"userConsent"}
			label={
				<>
					I consent to TQL’s collection and use of my personal information as
					more fully set forth in TQL&apos;s{" "}
					<a
						className={"tql-link"}
						href={privacyUrl}
						rel={"noreferrer"}
						target={"_blank"}
					>
						Privacy and Cookies Policy
					</a>
					, and I agree to TQL&apos;s{" "}
					<a
						className={"tql-link"}
						href={privacyUrl}
						rel={"noreferrer"}
						target={"_blank"}
					>
						Privacy and Cookies Policy
					</a>
					, and{" "}
					<a
						className={"tql-link"}
						href={termsUrl}
						rel={"noreferrer"}
						target={"_blank"}
					>
						Terms of Use
					</a>
					.
				</>
			}
			labelPlacement={"after"}
			name={"userConsent"}
			required={required}
			validator={validator}
		/>
	);
};

function getRoleId(role: string): number {
	switch (role) {
		case "Admin": {
			return 1;
		}
		case "Finance": {
			return 2;
		}
		case "Reporting": {
			return 3;
		}
		case "Operations": {
			return 4;
		}
		default: {
			return 0;
		}
	}
}

const requiredFieldValidator = (value: string | undefined) =>
	value && value.trim() !== "" ? "" : "This field is required.";

function phoneValidator(value: string): string | undefined {
	if (!value) return "Phone number is required.";
	const digits = value.replaceAll(/\D/g, "");
	if (digits.length !== 10) {
		return "Please enter a valid 10-digit phone number.";
	}
	return undefined;
}

type LabeledPromise = { label: string; promise: Promise<unknown> };

function showResultToasts(
	results: PromiseSettledResult<unknown>[],
	labeledPromises: LabeledPromise[],
	showErrorFn: (msg: string) => void,
	showSuccessFn: (msg: string) => void,
): boolean {
	let hasFailure = false;
	for (const [index, result] of results.entries()) {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- index is guaranteed to be in bounds since results and labeledPromises have the same length
		const { label } = labeledPromises[index]!;
		if (result.status === "rejected") {
			hasFailure = true;
			console.error(result.reason);
			showErrorFn(`${label}: Failed to save. Please try again.`);
		} else {
			showSuccessFn(`${label}: Saved successfully.`);
		}
	}
	return hasFailure;
}

export const MySettings = () => {
	const { isInternal } = useDeploymentType();
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [showAddNumber, setShowAddNumber] = useState(false);
	const [newPhoneNumber, setNewPhoneNumber] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [mfaValidationError, setMfaValidationError] = useState("");

	// Local MFA state (staged changes, not yet saved)
	const [mfaOptIn, setMfaOptIn] = useState<boolean | null>(null);
	const [pendingAddNumbers, setPendingAddNumbers] = useState<string[]>([]);
	const [pendingRemoveIds, setPendingRemoveIds] = useState<Set<string>>(
		new Set(),
	);

	const { data: userInfo, isLoading } = useGetCurrentUserInfo(isInternal);
	const updateMutation = useUpdateWebUser();

	const { data: mfaPhoneNumbers, isLoading: isMfaLoading } =
		useGetMfaPhoneNumbers();
	const enableMfaMutation = useEnableMfa();
	const disableMfaMutation = useDisableMfa();
	const addPhoneNumberMutation = useAddMfaPhoneNumber();
	const removePhoneNumberMutation = useRemoveMfaPhoneNumber();
	const invalidateMfaQueries = useInvalidateMfaQueries();
	const { showError, showSuccess } = useNotificationActions();

	// Sync local mfaOptIn from server when data loads
	const serverMfaEnabled = userInfo?.isMfaEnabled ?? false;
	useEffect(() => {
		setMfaOptIn(serverMfaEnabled);
	}, [serverMfaEnabled]);

	const isMfaEnabled = mfaOptIn ?? serverMfaEnabled;

	// Effective phone number list: server numbers (minus pending removes) + pending adds
	const activeServerNumbers = useMemo(
		() =>
			(mfaPhoneNumbers ?? []).filter(
				(p) => !pendingRemoveIds.has(p.userMfaPhoneNumberId),
			),
		[mfaPhoneNumbers, pendingRemoveIds],
	);

	const totalActiveNumbers =
		activeServerNumbers.length + pendingAddNumbers.length;

	const mfaOptInChanged = isMfaEnabled !== serverMfaEnabled;

	const initialValues = useMemo(() => {
		if (!userInfo) return undefined;
		return {
			firstName: userInfo.firstName,
			lastName: userInfo.lastName,
			email: userInfo.email,
			permissionLevel: userInfo.role,
		};
	}, [userInfo]);

	const handleSubmit = async (dataItem: Record<string, unknown>) => {
		if (!userInfo) return;

		if (isMfaEnabled && totalActiveNumbers === 0) {
			setMfaValidationError(
				"At least one MFA phone number is required when MFA is enabled.",
			);
			return;
		}
		setMfaValidationError("");
		setIsSaving(true);

		const labeledPromises: LabeledPromise[] = [];

		// Only update user info if it has changed
		const newFirstName = dataItem["firstName"] as string;
		const newLastName = dataItem["lastName"] as string;
		const userInfoChanged =
			newFirstName !== userInfo.firstName || newLastName !== userInfo.lastName;

		if (userInfoChanged) {
			const role = (dataItem["permissionLevel"] as string) || userInfo.role;
			const roleId = getRoleId(role);

			const body: UpdateWebUserBody = {
				Errors: "",
				Role: "",
				Roles: { Role: role, RoleValue: roleId },
				email: userInfo.email,
				firstName: newFirstName,
				lastName: newLastName,
				mobilePhoneNumber: userInfo.mobilePhoneNumber || "",
				roleId,
				rowId: "",
				userId: userInfo.userId,
			};

			labeledPromises.push({
				label: "User info",
				promise: updateMutation.mutateAsync(body),
			});
		}

		// MFA enable/disable
		if (mfaOptInChanged) {
			if (isMfaEnabled) {
				labeledPromises.push({
					label: "Enable MFA",
					promise: enableMfaMutation.mutateAsync(undefined),
				});
			} else {
				labeledPromises.push({
					label: "Disable MFA",
					promise: disableMfaMutation.mutateAsync(undefined),
				});
			}
		}

		// Add staged phone numbers
		for (const number of pendingAddNumbers) {
			labeledPromises.push({
				label: `Add phone number ${maskPhoneDisplay(number)}`,
				promise: addPhoneNumberMutation.mutateAsync(number),
			});
		}

		// Remove staged phone numbers
		for (const phoneNumberId of pendingRemoveIds) {
			labeledPromises.push({
				label: "Remove phone number",
				promise: removePhoneNumberMutation.mutateAsync(phoneNumberId),
			});
		}

		if (labeledPromises.length === 0) {
			setIsSaving(false);
			return;
		}

		const results = await Promise.allSettled(
			labeledPromises.map((lp) => lp.promise),
		);

		const hasFailure = showResultToasts(
			results,
			labeledPromises,
			showError,
			showSuccess,
		);

		invalidateMfaQueries();

		if (!hasFailure) {
			setPendingAddNumbers([]);
			setPendingRemoveIds(new Set());
			setSuccessMessage("Your settings have been saved successfully.");
			setShowSuccessModal(true);
		}

		setIsSaving(false);
	};

	const handleCloseSuccessModal = useCallback(() => {
		setShowSuccessModal(false);
	}, []);

	const handleMfaOptInChange = useCallback(() => {
		setMfaOptIn((prev) => !(prev ?? serverMfaEnabled));
		setMfaValidationError("");
	}, [serverMfaEnabled]);

	const handleStagePhoneNumber = useCallback(() => {
		const error = phoneValidator(newPhoneNumber);
		if (error) return;

		const e164Number = formatPhoneForApi(newPhoneNumber);
		setPendingAddNumbers((prev) => [...prev, e164Number]);
		setNewPhoneNumber("");
		setShowAddNumber(false);
		setMfaValidationError("");
	}, [newPhoneNumber]);

	const handleUnstagePhoneNumber = useCallback((index: number) => {
		setPendingAddNumbers((prev) => prev.filter((_, i) => i !== index));
	}, []);

	const handleRemoveServerNumber = useCallback((phoneNumberId: string) => {
		setPendingRemoveIds((prev) => new Set(prev).add(phoneNumberId));
	}, []);

	return (
		<div className={styles.mySettingsWrapper}>
			{showSuccessModal ? (
				<TraxModal
					confirmText={"OK"}
					onClose={handleCloseSuccessModal}
					onConfirm={handleCloseSuccessModal}
					showCancelButton={false}
					showConfirmationButtons={true}
					title={"Success"}
					type={"success"}
				>
					<p>{successMessage}</p>
				</TraxModal>
			) : null}
			<TraxPageHeader showSupportMessage title={"My Settings"} />
			<TraxCard title={"My Information"}>
				<Form
					key={userInfo ? "loaded" : "empty"}
					initialValues={initialValues}
					onSubmit={handleSubmit}
					render={(formRenderProps: FormRenderProps) => (
						<FormElement>
							<div className={styles.userInfoRow}>
								<ValidationInputField
									fieldType={FieldType.TEXTBOX}
									id={"firstName"}
									label={"First Name"}
									loading={isLoading || isSaving}
									name={"firstName"}
									required={true}
									validator={requiredFieldValidator}
								/>
								<ValidationInputField
									fieldType={FieldType.TEXTBOX}
									id={"lastName"}
									label={"Last Name"}
									loading={isLoading || isSaving}
									name={"lastName"}
									required={true}
									validator={requiredFieldValidator}
								/>
								<ValidationInputField
									disabled={true}
									fieldType={FieldType.TEXTBOX}
									id={"email"}
									label={"Email"}
									loading={isLoading || isSaving}
									name={"email"}
									required={true}
								/>
								<ValidationInputField
									disabled={true}
									fieldType={FieldType.TEXTBOX}
									id={"permissionLevel"}
									label={"Permission Level"}
									loading={isLoading || isSaving}
									name={"permissionLevel"}
									required={true}
								/>
							</div>
							<div className={styles.securityWrapper}>
								<div className={styles.userSecurity}>
									<div className={styles.securityHeader}>User Security</div>
									<div>
										Multi-Factor Authentication (MFA) is the easiest and safest
										way to protect your account from fraud. By enabling this
										security feature, you will verify your identity at sign-in
										with a text message or phone call. You need at least one MFA
										protected phone number to opt in to MFA.
									</div>
									<div className={styles.optInRow}>
										<ValidationInputField
											checked={isMfaEnabled}
											disabled={isSaving}
											fieldType={FieldType.CHECKBOX}
											id={"mfaOptIn"}
											label={
												"Opt-in and protect your account with Multi-Factor Authentication (MFA)"
											}
											labelPlacement={"after"}
											name={"mfaOptIn"}
											onChange={handleMfaOptInChange}
										/>
										{!isMfaEnabled && serverMfaEnabled ? (
											<div className={styles.mfaOptOutWarning}>
												You have decided to opt OUT of Multi-Factor
												Authentication (MFA). By disabling this feature your
												account will no longer be protected by MFA.
											</div>
										) : null}
									</div>
								</div>
								<div className={styles.mfaPhoneNumbers}>
									<div className={styles.securityHeader}>
										MFA Phone Number(s)
									</div>
									{mfaValidationError ? (
										<div className={"k-text-error"} role={"alert"}>
											{mfaValidationError}
										</div>
									) : null}
									{isMfaLoading || isSaving ? (
										<Skeleton
											animation={{ type: "wave" }}
											shape={"rectangle"}
											style={{ width: "100%", height: 60, borderRadius: 3 }}
										/>
									) : (
										<div className={styles.phoneNumberList}>
											{activeServerNumbers.map((phone) => (
												<div
													key={phone.userMfaPhoneNumberId}
													className={styles.phoneNumberRow}
												>
													<div>{maskPhoneDisplay(phone.maskedPhoneNumber)}</div>
													<Button
														disabled={isSaving}
														onClick={() => {
															handleRemoveServerNumber(
																phone.userMfaPhoneNumberId,
															);
														}}
														variant={"secondary"}
													>
														Remove
													</Button>
												</div>
											))}
											{pendingAddNumbers.map((number, index) => (
												<div
													key={`pending-${index.toString()}`}
													className={styles.phoneNumberRow}
												>
													<div>{number}</div>
													<Button
														disabled={isSaving || !isMfaEnabled}
														onClick={() => {
															handleUnstagePhoneNumber(index);
														}}
														variant={"secondary"}
													>
														Remove
													</Button>
												</div>
											))}
											{totalActiveNumbers === 0 ? (
												<div>No phone numbers registered.</div>
											) : null}
										</div>
									)}
									{showAddNumber ? (
										<div className={styles.addNumberRow}>
											<div>
												<ValidationInputField
													fieldType={FieldType.MASKEDTEXTBOX}
													id={"newMfaPhoneNumber"}
													label={"Phone Number"}
													mask={PHONE_MASK}
													name={"newMfaPhoneNumber"}
													onChange={(e: { value: string }) => {
														setNewPhoneNumber(e.value);
													}}
													required={true}
													validator={phoneValidator}
													value={newPhoneNumber}
												/>
											</div>

											<Button
												disabled={
													!newPhoneNumber || !!phoneValidator(newPhoneNumber)
												}
												onClick={handleStagePhoneNumber}
												variant={"primary"}
											>
												Add
											</Button>
											<Button
												onClick={() => {
													setShowAddNumber(false);
													setNewPhoneNumber("");
												}}
												variant={"secondary"}
											>
												Cancel
											</Button>
										</div>
									) : (
										<Button
											disabled={isSaving || !isMfaEnabled}
											onClick={() => {
												setShowAddNumber(true);
											}}
											svgIcon={plusCircle}
											variant={"tertiary"}
										>
											Add Number
										</Button>
									)}
								</div>
							</div>
							<div className={styles.saveRow}>
								<ConsentStatement
									actionLabel={isSaving ? "Saving..." : "Save"}
								/>
								<Button
									disabled={
										!formRenderProps.allowSubmit || isSaving || isLoading
									}
									onClick={formRenderProps.onSubmit}
									variant={"primary"}
								>
									{isSaving ? "Saving..." : "Save"}
								</Button>
							</div>
						</FormElement>
					)}
				/>
			</TraxCard>
		</div>
	);
};
